import React, { useState, useEffect, useCallback } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { KeyRound, RefreshCw, Copy, Check, Eye, X, Send, Share2 } from "lucide-react";

const sfile = (name) => (name ? `${API}/files/${name}` : "");

function InviteModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(data.link); setCopied(true); };
  const text = encodeURIComponent(data.share_text || data.link);
  const lineUrl = `https://line.me/R/msg/text/?${text}`;
  const waUrl = `https://wa.me/?text=${text}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5" data-testid="invite-modal">
      <div className="bg-white text-gray-900 rounded-xl max-w-sm w-full p-6">
        <h3 className="text-lg font-semibold mb-1">登録リンクを送信</h3>
        <p className="text-sm text-gray-500 mb-4">入居者に下記リンクを送ってください。開くと暗証番号を設定してポータルを利用開始できます（有効期限7日間）。</p>
        <div className="bg-gray-100 rounded-lg p-3 mb-4 break-all text-xs" data-testid="invite-link">{data.link}</div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <a href={lineUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-md py-2.5 text-sm text-white" style={{ backgroundColor: "#06C755" }} data-testid="invite-line">
            <Send className="w-4 h-4" />LINEで送る
          </a>
          <a href={waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-md py-2.5 text-sm text-white" style={{ backgroundColor: "#25D366" }} data-testid="invite-whatsapp">
            <Share2 className="w-4 h-4" />WhatsApp
          </a>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-gray-300 rounded-md py-2 text-sm hover:bg-gray-50" data-testid="invite-copy">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}{copied ? "コピーしました" : "リンクをコピー"}
          </button>
          <button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-hover text-white rounded-md py-2 text-sm" data-testid="invite-close">閉じる</button>
        </div>
      </div>
    </div>
  );
}

function CredModal({ data, onClose }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(`${data.login_code} / ${data.pin}`); setCopied(true); };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5" data-testid="cred-modal">
      <div className="bg-white text-gray-900 rounded-xl max-w-sm w-full p-6">
        <h3 className="text-lg font-semibold mb-1">{t("ログイン情報を発行しました")}</h3>
        <p className="text-sm text-gray-500 mb-5">{t("暗証番号はこの画面でのみ表示されます。借主にお伝えください。")}</p>
        <div className="space-y-3">
          <div className="bg-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500">{t("ログインコード")}</div>
            <div className="text-xl font-mono font-semibold tracking-widest" data-testid="cred-code">{data.login_code}</div>
          </div>
          <div className="bg-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500">{t("暗証番号")}</div>
            <div className="text-xl font-mono font-semibold tracking-[0.3em]" data-testid="cred-pin">{data.pin}</div>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={copy} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-gray-300 rounded-md py-2 text-sm hover:bg-gray-50">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}{copied ? t("コピーしました") : t("コピー")}
          </button>
          <button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-hover text-white rounded-md py-2 text-sm" data-testid="cred-close">{t("閉じる")}</button>
        </div>
      </div>
    </div>
  );
}

function AgreementViewer({ cid, onClose, onApproved }) {
  const { t } = useI18n();
  const [ag, setAg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/tenant-admin/agreements/${cid}`); setAg(data); } catch { setAg(false); } })(); }, [cid]);
  const approve = async () => { setBusy(true); try { await api.post(`/tenant-admin/agreements/${cid}/approve`); toast.success(t("承認しました")); onApproved(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };

  const row = (k, v) => v ? <div className="flex justify-between gap-4 py-1 text-sm border-b border-gray-100"><span className="text-gray-500">{t(k)}</span><span className="font-medium text-right">{v}</span></div> : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-auto" data-testid="agreement-viewer">
      <div className="bg-white text-gray-900 rounded-xl max-w-2xl w-full p-6 my-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t("提出内容の確認")}</h3>
          <button onClick={onClose} data-testid="viewer-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        {ag === null ? <div className="text-gray-500 py-10 text-center">…</div> : !ag ? <div className="text-gray-500 py-10 text-center">{t("提出内容が見つかりません")}</div> : (
          <div className="space-y-4">
            <div className="text-sm"><span className="px-2 py-0.5 rounded-full bg-gray-100">{t(ag.account_type === "individual" ? "個人" : "法人")}</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${ag.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{t(ag.status === "approved" ? "承認済み" : "提出済み")}</span></div>
            <div>
              {ag.account_type === "individual" ? (<>
                {row("氏名", ag.individual?.name)}{row("生年月日", ag.individual?.birth_date)}{row("電話番号", ag.individual?.phone)}{row("住所", ag.individual?.address)}
              </>) : (<>
                {row("会社名", ag.corporate?.company_name)}{row("登記番号", ag.corporate?.registration_number)}{row("代表者名", ag.corporate?.representative_name)}{row("担当者名", ag.corporate?.contact_name)}
              </>)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["本人写真/担当者写真", ag.photo], ["身分証（表）", ag.id_front], ["身分証（裏）", ag.id_back], ["サイン", ag.signature]].map(([lbl, nm]) => nm && (
                <a key={lbl} href={sfile(nm)} target="_blank" rel="noreferrer" className="block">
                  <div className="text-xs text-gray-500 mb-1">{lbl}</div>
                  <img src={sfile(nm)} alt={lbl} className="w-full h-24 object-contain border border-gray-200 rounded bg-gray-50" />
                </a>
              ))}
            </div>
            <a href={`${API}/tenant-admin/agreements/${cid}/pdf`} target="_blank" rel="noreferrer" className="w-full block text-center border border-gray-300 rounded-md py-2.5 text-sm hover:bg-gray-50" data-testid="agreement-pdf">{t("PDF出力")}</a>
            {ag.status !== "approved" && (
              <button onClick={approve} disabled={busy} className="w-full bg-brand hover:bg-brand-hover text-white rounded-md py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="agreement-approve">
                <Check className="w-4 h-4" />{t("承認する")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TenantAccess() {
  const { t } = useI18n();
  const [tenants, setTenants] = useState([]);
  const [agreements, setAgreements] = useState({});
  const [cred, setCred] = useState(null);
  const [invite, setInvite] = useState(null);
  const [viewCid, setViewCid] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, aRes] = await Promise.all([api.get("/tenants"), api.get("/tenant-admin/agreements")]);
      setTenants(tRes.data);
      const map = {};
      for (const a of aRes.data) map[a.tenant_id] = a;
      setAgreements(map);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const issue = async (id) => { try { const { data } = await api.post(`/tenant-admin/tenants/${id}/issue-credentials`); setCred(data); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const reset = async (id) => { try { const { data } = await api.post(`/tenant-admin/tenants/${id}/reset-pin`); setCred(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const sendInvite = async (id) => { try { const { data } = await api.post(`/tenant-admin/tenants/${id}/invite`); setInvite(data); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6" data-testid="tenant-access-page">
      <div>
        <h1 className="text-2xl font-semibold">{t("借主ポータル")}</h1>
        <p className="text-sm text-muted-foreground">{t("借主のログイン情報の発行と、提出されたデジタル契約の確認・承認")}</p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3">{t("氏名")}</th>
              <th className="px-4 py-3">{t("ログインコード")}</th>
              <th className="px-4 py-3">{t("状態")}</th>
              <th className="px-4 py-3">{t("提出内容")}</th>
              <th className="px-4 py-3 text-right">{t("操作")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">…</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">{t("入居者が登録されていません。")}</td></tr>
            ) : tenants.map((tn) => {
              const ag = agreements[tn.id];
              return (
                <tr key={tn.id} className="border-t" data-testid={`ta-row-${tn.id}`}>
                  <td className="px-4 py-3 font-medium">{tn.name}</td>
                  <td className="px-4 py-3 font-mono">{tn.login_code || <span className="text-muted-foreground">-</span>}</td>
                  <td className="px-4 py-3">{tn.portal_active ? <span className="text-green-600">{t("有効")}</span> : <span className="text-muted-foreground">{t("未発行")}</span>}</td>
                  <td className="px-4 py-3">
                    {ag ? (
                      <button onClick={() => setViewCid(ag.contract_id)} className="inline-flex items-center gap-1.5 text-brand hover:underline" data-testid={`ta-view-${tn.id}`}>
                        <Eye className="w-4 h-4" />{t(ag.status === "approved" ? "承認済み" : "提出済み")}
                      </button>
                    ) : <span className="text-muted-foreground">{t("未提出")}</span>}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => sendInvite(tn.id)} className="inline-flex items-center gap-1 text-xs bg-emerald-600 text-white rounded px-2.5 py-1.5 hover:bg-emerald-700" data-testid={`ta-invite-${tn.id}`}><Send className="w-3.5 h-3.5" />{t("招待リンク")}</button>
                    {tn.login_code ? (
                      <button onClick={() => reset(tn.id)} className="inline-flex items-center gap-1 text-xs border rounded px-2.5 py-1.5 hover:bg-muted" data-testid={`ta-reset-${tn.id}`}><RefreshCw className="w-3.5 h-3.5" />{t("PIN再発行")}</button>
                    ) : (
                      <button onClick={() => issue(tn.id)} className="inline-flex items-center gap-1 text-xs bg-brand text-white rounded px-2.5 py-1.5 hover:bg-brand-hover" data-testid={`ta-issue-${tn.id}`}><KeyRound className="w-3.5 h-3.5" />{t("ポータル発行")}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {cred && <CredModal data={cred} onClose={() => setCred(null)} />}
      {invite && <InviteModal data={invite} onClose={() => setInvite(null)} />}
      {viewCid && <AgreementViewer cid={viewCid} onClose={() => setViewCid(null)} onApproved={() => { setViewCid(null); load(); }} />}
    </div>
  );
}
